import { GUEST_COLOR, MEMBER_COLORS } from '@awwyeah/shared';

/**
 * A word for a player's colour, so "which one am I?" has an answer you can read, not just a
 * swatch to squint at (owner ask 2026-09-25: coming back to a game after a month). Pieces on
 * every board are drawn in the *member's* colour, so this names that colour.
 *
 * The household palette (`MEMBER_COLORS`) is named exactly; any other hex (the household
 * schema allows one) gets the nearest palette name — close enough to point at the right
 * pieces, which is all this is for.
 */
const NAMES: Record<string, string> = {
  '#ef4444': 'Red',
  '#f97316': 'Orange',
  '#f59e0b': 'Amber',
  '#eab308': 'Yellow',
  '#84cc16': 'Lime',
  '#22c55e': 'Green',
  '#10b981': 'Emerald',
  '#14b8a6': 'Teal',
  '#06b6d4': 'Cyan',
  '#0ea5e9': 'Sky',
  '#3b82f6': 'Blue',
  '#6366f1': 'Indigo',
  '#8b5cf6': 'Violet',
  '#d946ef': 'Fuchsia',
  '#ec4899': 'Pink',
  '#f43f5e': 'Rose',
  [GUEST_COLOR.toLowerCase()]: 'Slate',
};

function rgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function colorName(hex: string): string {
  const key = hex.trim().toLowerCase();
  const exact = NAMES[key];
  if (exact) return exact;
  const target = rgb(key);
  if (!target) return 'Your colour';
  let best = 'Your colour';
  let bestDist = Number.POSITIVE_INFINITY;
  for (const candidate of [...MEMBER_COLORS, GUEST_COLOR]) {
    const c = rgb(candidate);
    if (!c) continue;
    const dist = (c[0] - target[0]) ** 2 + (c[1] - target[1]) ** 2 + (c[2] - target[2]) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = NAMES[candidate.toLowerCase()] ?? best;
    }
  }
  return best;
}
